<template>
  <LmDialog v-model="modalShow" :title="title" :styles="{ height: '100%' }">
    <template #content>
      <q-card-section class="dialog-content">
        <AgGridWrapper
          ref="agGrid"
          :options="gridOptions"
          :title="`Showing ${selectedStatus.label} subscriptions`"
          :infinite-scroll-request="infiniteScrollRequest"
          :update-rows-count="updateRowsCount"
        >
          <template #headerActions>
            <div class="choose-status text-center">
              <q-chip
                v-for="avStatus of availableStatuses"
                :key="avStatus.id"
                :selected="avStatus.value === selectedStatus.value"
                :outline="avStatus.value !== selectedStatus.value"
                color="primary"
                text-color="white"
                clickable
                @click="changeStatus(avStatus)"
              >
                {{ avStatus.label }}
              </q-chip>
            </div>
          </template>
          <template #footerActions>
            <div class="row fit q-pr-md q-pb-md justify-end">Total Rows: {{ totalRows }}</div>
          </template>
        </AgGridWrapper>
      </q-card-section>
    </template>
  </LmDialog>
</template>

<script>
import * as apiTo from '@/modular-api'
import { dateColumn, renderEndDate } from '@lib/reusable-ag-grid-columns'
import { dateRenderer, priceWithCurrencyRenderer, numericSorter } from '@lib/generic.utils'
import { formatPrice } from '@lib/formatting.utils'
// Components
import LmDialog from '@/reusable/base-components/LmDialog/LmDialog'
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import LmGridEntityLink from '@components/ag-grid-helpers/EntityLink'
import AgGridTextDateFilter from '@components/ag-grid-helpers/AgGridTextDateFilter'
import AgGridPriceFilter from '@components/ag-grid-helpers/AgGridPriceFilter'

export default {
  components: {
    LmDialog,
    AgGridWrapper
  },

  props: {
    value: Boolean,
    status: String,
    title: String
  },

  data: () => ({
    selectedStatus: {},
    availableStatuses: [
      { id: 1, label: 'Accepted', value: 'accepted' },
      { id: 2, label: 'Cancelled', value: 'cancelled' },
      { id: 3, label: 'Pending', value: 'pending' }
    ],
    totalRows: null
  }),

  computed: {
    modalShow: {
      get() {
        return this.value
      },

      set(val) {
        this.$emit('input', val)
      }
    },

    gridOptions() {
      return {
        columnDefs: [
          {
            headerName: 'Subscription Name',
            field: 'subscription.product.name',
            tooltipField: 'subscription.product.name',
            cellRendererParams: params => {
              if (!params.data) return
              if (!params || !params.value) return
              return {
                customData: {
                  id: params.data.subscription.id,
                  routeName: 'subscription',
                  label: params.value,
                  newTab: false
                }
              }
            },
            cellRendererFramework: LmGridEntityLink
          },
          {
            headerName: 'Publisher',
            field: 'publisher.name',
            tooltipField: 'publisher.name'
          },
          {
            headerName: 'Vendor',
            field: 'vendor.name',
            tooltipField: 'vendor.name'
          },
          dateColumn({
            headerName: 'Start date',
            field: 'start_date',
            valueGetter: params => {
              if (!params.data) return
              return dateRenderer(params.data.start_date)
            },
            cellRenderer: params => dateRenderer(params.data.start_date),
            tooltipValueGetter: params => {
              if (!params.data) return
              return dateRenderer(params.data.start_date)
            }
          }),
          dateColumn({
            headerName: 'End date',
            field: 'end_date',
            cellRenderer: renderEndDate,
            tooltipValueGetter: renderEndDate,
            valueGetter: renderEndDate
          }),
          {
            headerName: 'Price in Original currency',
            field: 'price',
            maxWidth: 250,
            headerClass: 'price-column',
            cellStyle: { textAlign: 'right', paddingRight: '15px !important' },
            filter: 'agNumberColumnFilter',
            floatingFilterComponent: 'AgGridPriceFilter',
            filterParams: {
              fieldId: 'ag-price'
            },
            comparator: (a, b, nodeA, nodeB) => {
              return numericSorter(nodeA.data.price, nodeB.data.price)
            },
            cellRenderer: params => {
              if (!params.data) return
              params.value = params.data.price
              return priceWithCurrencyRenderer(params, 'currency')
            },
            tooltipValueGetter: params => {
              if (!params.data) return
              params.value = params.data.price
              return priceWithCurrencyRenderer(params, 'currency', true)
            },
            valueGetter: params => {
              if (!params.data) return
              return formatPrice(params.data.price) || ''
            }
          },
          {
            headerName: 'Price in Local currency',
            field: 'normalized_price',
            maxWidth: 250,
            headerClass: 'price-column',
            cellStyle: { textAlign: 'right', paddingRight: '15px !important' },
            filter: 'agNumberColumnFilter',
            floatingFilterComponent: 'AgGridPriceFilter',
            comparator: (a, b, nodeA, nodeB) => {
              return numericSorter(nodeA.data.price, nodeB.data.price)
            },
            cellRenderer: params => {
              if (!params.data) return
              params.value = params.data.normalized_price
              return priceWithCurrencyRenderer(params, 'normalized_currency')
            },
            tooltipValueGetter: params => {
              if (!params.data) return
              params.value = params.data.normalized_price
              return priceWithCurrencyRenderer(params, 'normalized_currency', true)
            },
            filterParams: {
              valueGetter: params => {
                if (!params.data) return
                if (params.data.normalized_price) {
                  return `${formatPrice(params.data.normalized_price)} ${params.data.normalized_currency}`
                }
                return 'N/A'
              },
              fieldId: 'ag-local-price'
            },
            valueGetter: params => {
              if (!params.data) return
              return formatPrice(params.data.normalized_price) || ''
            }
          }
        ],
        frameworkComponents: {
          AgGridTextDateFilter,
          AgGridPriceFilter
        },
        defaultColDef: {
          floatingFilter: true,
          filter: 'agTextColumnFilter',
          suppressMenu: true,
          floatingFilterComponentParams: {
            debounceMs: 800
          }
        },
        cacheBlockSize: 40,
        pagination: false,
        rowModelType: 'serverSide',
        serverSideStoreType: 'partial',
        domLayout: 'normal'
      }
    }
  },

  watch: {
    value(showModal) {
      if (showModal) {
        this.selectedStatus = {
          ...this.availableStatuses.find(s => s.value === this.status)
        }
      }
    }
  },

  methods: {
    infiniteScrollRequest(request) {
      return apiTo.GET_PAGINATED_RENEWAL_STATUS(request, this.selectedStatus.value)
    },

    updateRowsCount(rowsCount) {
      this.totalRows = rowsCount
    },

    changeStatus(newStatus) {
      this.selectedStatus = newStatus
      this.$refs.agGrid.purgeServerSideCache()
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/stylus/mixins.styl'

styledScrollFor('>>> .ag-body-viewport.ag-layout-normal');

>>> .q-chip__icon {
  font-size 1rem
  font-weight 300
}

>>> .table-content {
  height calc(100% - 90px)
  padding 0 40px

  .ag-theme-material {
    height  100%
  }

  .ag-center-cols-viewport {
    overflow-x hidden
  }
}

.dialog-card {
  width: 1200px;
  max-width: 1200px;
}

.dialog-content {
  display grid
  gap 15px
  padding 25px
}
</style>
